import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Footer from "../components/Footer";
import {
  faCode,
  faMobileAlt,
  faDatabase,
  faCreditCard,
  faCheckCircle,
  faDiagramProject,
  faClockRotateLeft,
  faAward,
} from "@fortawesome/free-solid-svg-icons";
import "./Home.css";

// 1. Direct Imports from src/assets/sliders
import slider1 from "../assets/sliders/slider1.jpeg";
import slider2 from "../assets/sliders/slider10.png";
import slider3 from "../assets/sliders/slide3.jpg";
import slider4 from "../assets/sliders/slider9.png";
import slider5 from "../assets/sliders/slider11.png";
import slider6 from "../assets/sliders/slider12.png";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const sliderImages = [slider1, slider2, slider3, slider4, slider5, slider6];

const fallbackProjects = [
  {
    name: "FinGrow Micro-Fintech",
    description:
      "Automated micro-savings and credit monitoring system featuring M-Pesa STK push processing.",
    image:
      "https://images.unsplash.com/photo-1563013544-824ae1d704d3?auto=format&fit=crop&q=80&w=600",
    link: "/portfolio",
  },
  {
    name: "Local Service-Hub",
    description:
      "Hyper-local marketplace linking verified local service providers with clients in real-time.",
    image:
      "https://images.unsplash.com/photo-1581092921461-eab62e97a780?auto=format&fit=crop&q=80&w=600",
    link: "/portfolio",
  },
  {
    name: "ElectroMart Marketplace",
    description:
      "Scalable digital e-commerce web application with real-time cart logic and instant checkout.",
    image:
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=600",
    link: "/portfolio",
  },
];

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  const stats = [
    {
      icon: faCheckCircle,
      number: "10+",
      label: "Completed Projects",
      detail: "Deployed to production",
    },
    {
      icon: faClockRotateLeft,
      number: "3+",
      label: "In Progress",
      detail: "Active pipelines & architectures",
    },
    {
      icon: faDiagramProject,
      number: "99%",
      label: "Satisfied Clients",
      detail: "Regional & enterprise partners",
    },
    {
      icon: faAward,
      number: "2+",
      label: "Years of Delivery",
      detail: "Continuous technical engineering",
    },
  ];

  const services = [
    {
      icon: faCode,
      title: "Web Development",
      desc: "Custom, responsive web applications built with modern frameworks for maximum speed, security, and performance.",
    },
    {
      icon: faMobileAlt,
      title: "Mobile Apps",
      desc: "Cross-platform mobile solutions delivering fluid user interactions across both iOS and Android architectures.",
    },
    {
      icon: faDatabase,
      title: "Database Optimization",
      desc: "Structuring and tuning relational and NoSQL layers for sub-second queries, data integrity, and high concurrency.",
    },
    {
      icon: faCreditCard,
      title: "Payment Integration",
      desc: "Specialized in Safaricom Daraja M-Pesa STK Push, C2B/B2C automation, and secure payment reconciliation.",
    },
  ];

  const workSteps = [
    {
      num: "01",
      title: "Discovery & Architecture",
      desc: "Evaluating technical scope, business objectives, and security boundaries to draft strict system specifications.",
    },
    {
      num: "02",
      title: "Engineering Roadmap",
      desc: "Designing responsive UI wireframes, relational database schemas, and modular API contracts with set milestones.",
    },
    {
      num: "03",
      title: "Sprint Development",
      desc: "Developing clean, performant full-stack codebases paired with unit tests, CI/CD routines, and complete transparency.",
    },
    {
      num: "04",
      title: "Audit & Cloud Launch",
      desc: "Conducting vulnerability tests and server hardening before rolling out to production VPS infrastructure.",
    },
  ];

  // 1. Image Slider Timer
  useEffect(() => {
    const slideInterval = setInterval(() => {
      setCurrentSlide((prevIndex) => (prevIndex + 1) % sliderImages.length);
    }, 2000);

    return () => clearInterval(slideInterval);
  }, []);

  // 2. Scroll-triggered Reveal Animations (Intersection Observer)
  useEffect(() => {
    const animatedElements = document.querySelectorAll(".reveal-on-scroll");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: "0px 0px -40px 0px",
      }
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [featuredProjects]);

  // 3. Analytics Tracking & Portfolio Fetch
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        await axios.post(`${API_BASE_URL}/analytics/track-visit`, {
          path: window.location.pathname,
        });
      } catch (err) {
        // Fail silently
      }
    };

    const fetchLatestProjects = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/projects`);
        if (response.data && response.data.length > 0) {
          const latestThree = [...response.data].reverse().slice(0, 3);
          setFeaturedProjects(latestThree);
        } else {
          setFeaturedProjects(fallbackProjects);
        }
      } catch (error) {
        console.error("Error fetching homepage featured projects:", error);
        setFeaturedProjects(fallbackProjects);
      }
    };

    trackVisitor();
    fetchLatestProjects();
  }, []);

  return (
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero">
        <div className="container hero-flex">
          <div className="hero-text reveal-on-scroll slide-left">
            <h1>
              Innovating the Future with{" "}
              <span className="gradient-text">Stemiot Softwares</span>
            </h1>
            <p>
              We craft high-performance digital solutions that scale your
              business. From full-stack web apps to seamless payment
              integrations.
            </p>
            <div className="hero-btns">
              <Link to="/portfolio" className="btn-primary">
                View Our Work
              </Link>
              <Link to="/contact" className="btn-outline">
                Contact Us
              </Link>
            </div>
          </div>

          <div className="hero-image reveal-on-scroll slide-right">
            <div className="hero-slider-stage">
              {sliderImages.map((imgSrc, index) => (
                <img
                  key={index}
                  src={imgSrc}
                  alt={`Stemiot Showcase ${index + 1}`}
                  className={`hero-slider-img ${
                    index === currentSlide ? "active" : ""
                  }`}
                  loading={index === 0 ? "eager" : "lazy"}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Metrics & Counts Section (Before Services) */}
      <section className="home-stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((item, index) => (
              <div
                key={index}
                className="stat-card reveal-on-scroll pop-in"
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                <div className="stat-icon-wrap">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="stat-content">
                  <div className="stat-number">{item.number}</div>
                  <h3 className="stat-label">{item.label}</h3>
                  <p className="stat-detail">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services" id="services">
        <div className="container">
          <div className="section-title reveal-on-scroll slide-up">
            <h2>
              Our <span>Services</span>
            </h2>
            <p>Expert solutions tailored to your technical needs.</p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card reveal-on-scroll slide-up"
                style={{ transitionDelay: `${index * 140}ms` }}
              >
                <div className="service-icon">
                  <FontAwesomeIcon icon={service.icon} />
                </div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working Process / How We Work Section (After Services) */}
      <section className="how-we-work-section">
        <div className="container">
          <div className="section-title reveal-on-scroll slide-up">
            <h2>
              How We <span>Work</span>
            </h2>
            <p>
              A disciplined, engineering-first methodology ensuring zero downtime,
              transparent delivery, and architectures built for long-term scale.
            </p>
          </div>

          {/* Lifecycle Cycle Diagram */}
          <div className="process-lifecycle-diagram reveal-on-scroll pop-in">
            <svg
              className="lifecycle-svg"
              viewBox="0 0 1000 240"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Connection Pipeline Track */}
              <path
                d="M 125 120 L 375 120 L 625 120 L 875 120"
                stroke="#2F296F"
                strokeWidth="4"
                strokeDasharray="8 8"
                opacity="0.35"
              />

              {/* Stage 1: Discovery */}
              <g className="lifecycle-node">
                <circle cx="125" cy="120" r="44" fill="#FFFDF9" stroke="#2F296F" strokeWidth="4" />
                <circle cx="125" cy="120" r="34" fill="#2F296F" opacity="0.1" />
                <text x="125" y="115" textAnchor="middle" fill="#800020" fontSize="18" fontWeight="800">01</text>
                <text x="125" y="133" textAnchor="middle" fill="#2F296F" fontSize="11" fontWeight="700" letterSpacing="1">DISCOVER</text>
                <text x="125" y="195" textAnchor="middle" fill="#1E1A17" fontSize="13" fontWeight="700">Requirements & Audit</text>
              </g>

              {/* Arrow 1 */}
              <polygon points="255,115 267,120 255,125" fill="#800020" />

              {/* Stage 2: Architecture */}
              <g className="lifecycle-node">
                <circle cx="375" cy="120" r="44" fill="#FFFDF9" stroke="#800020" strokeWidth="4" />
                <circle cx="375" cy="120" r="34" fill="#800020" opacity="0.1" />
                <text x="375" y="115" textAnchor="middle" fill="#2F296F" fontSize="18" fontWeight="800">02</text>
                <text x="375" y="133" textAnchor="middle" fill="#800020" fontSize="11" fontWeight="700" letterSpacing="1">DESIGN</text>
                <text x="375" y="195" textAnchor="middle" fill="#1E1A17" fontSize="13" fontWeight="700">Database & Schemas</text>
              </g>

              {/* Arrow 2 */}
              <polygon points="505,115 517,120 505,125" fill="#800020" />

              {/* Stage 3: Engineering */}
              <g className="lifecycle-node">
                <circle cx="625" cy="120" r="44" fill="#FFFDF9" stroke="#2F296F" strokeWidth="4" />
                <circle cx="625" cy="120" r="34" fill="#2F296F" opacity="0.1" />
                <text x="625" y="115" textAnchor="middle" fill="#800020" fontSize="18" fontWeight="800">03</text>
                <text x="625" y="133" textAnchor="middle" fill="#2F296F" fontSize="11" fontWeight="700" letterSpacing="1">DEVELOP</text>
                <text x="625" y="195" textAnchor="middle" fill="#1E1A17" fontSize="13" fontWeight="700">API & Frontend Sprints</text>
              </g>

              {/* Arrow 3 */}
              <polygon points="755,115 767,120 755,125" fill="#800020" />

              {/* Stage 4: Production Deployment */}
              <g className="lifecycle-node">
                <circle cx="875" cy="120" r="44" fill="#FFFDF9" stroke="#800020" strokeWidth="4" />
                <circle cx="875" cy="120" r="34" fill="#800020" opacity="0.1" />
                <text x="875" y="115" textAnchor="middle" fill="#2F296F" fontSize="18" fontWeight="800">04</text>
                <text x="875" y="133" textAnchor="middle" fill="#800020" fontSize="11" fontWeight="700" letterSpacing="1">DEPLOY</text>
                <text x="875" y="195" textAnchor="middle" fill="#1E1A17" fontSize="13" fontWeight="700">Hardening & Launch</text>
              </g>
            </svg>
          </div>

          {/* Process Breakdown Grid */}
          <div className="process-matrix">
            <div className="process-row">
              <div className="process-item reveal-on-scroll slide-left">
                <div className="process-number">{workSteps[0].num}</div>
                <div className="process-text">
                  <h3>{workSteps[0].title}</h3>
                  <p>{workSteps[0].desc}</p>
                </div>
              </div>

              <div className="process-item reveal-on-scroll slide-right">
                <div className="process-number">{workSteps[1].num}</div>
                <div className="process-text">
                  <h3>{workSteps[1].title}</h3>
                  <p>{workSteps[1].desc}</p>
                </div>
              </div>
            </div>

            <div className="process-row">
              <div className="process-item reveal-on-scroll slide-left">
                <div className="process-number">{workSteps[2].num}</div>
                <div className="process-text">
                  <h3>{workSteps[2].title}</h3>
                  <p>{workSteps[2].desc}</p>
                </div>
              </div>

              <div className="process-item reveal-on-scroll slide-right">
                <div className="process-number">{workSteps[3].num}</div>
                <div className="process-text">
                  <h3>{workSteps[3].title}</h3>
                  <p>{workSteps[3].desc}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Portfolio Section */}
      <section className="home-portfolio-featured">
        <div className="container">
          <div className="section-title reveal-on-scroll slide-up">
            <h2>
              Featured <span>Work</span>
            </h2>
            <p>
              A glimpse into our recent production architectures and client
              solutions.
            </p>
          </div>

          <div className="home-portfolio-grid">
            {featuredProjects &&
              featuredProjects.slice(0, 3).map((project, idx) => (
                <article
                  className="home-portfolio-card reveal-on-scroll pop-in"
                  style={{ transitionDelay: `${idx * 150}ms` }}
                  key={project._id || idx}
                >
                  <div className="home-portfolio-img-wrapper">
                    <img src={project.image} alt={project.name} />
                    <div className="home-portfolio-overlay">
                      <a
                        href={project.link || "/portfolio"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="home-overlay-icon"
                        aria-label={`View ${project.name}`}
                      >
                        ↗
                      </a>
                    </div>
                  </div>

                  <div className="home-portfolio-content">
                    {project.tags && project.tags.length > 0 && (
                      <div className="home-portfolio-tags">
                        {project.tags.map((tag, tIdx) => (
                          <span className="home-tag" key={tIdx}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <h3>{project.name}</h3>
                    <p>{project.description}</p>
                  </div>
                </article>
              ))}
          </div>

          <div className="view-more-container reveal-on-scroll slide-up">
            <Link to="/portfolio" className="btn-view-more">
              View Full Showcase <span>→</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;